import { ExtractionStatus, type FileExtension, isExtractableExtension } from "@inrev/common";
import { FC, useId } from "react";
import { ConfirmationModalClickWrapper } from "../layout/ConfirmationModalClickWrapper";
import { ClickableIcon } from "../primitives";
import { PortaledTooltip } from "../ui/Tooltip";
import { UploadedDocumentRow } from "./types";

const iconButtonClass =
	"flex h-6 w-6 shrink-0 items-center justify-center rounded text-tertiary hover:bg-surface-neutral hover:text-secondary";

type ExtractionButtonProps = {
	row: UploadedDocumentRow;
	onTrigger: (row: UploadedDocumentRow) => void;
	extractionStatus?: ExtractionStatus;
	triggerPending: boolean;
	hasExistingExtraction: boolean;
	/** Defaults to PDF-only; WIP passes isWipExtractableExtension so Excel schedules can re-extract. */
	isExtensionSupported?: (extension: FileExtension) => boolean;
};

export const ExtractionButton: FC<ExtractionButtonProps> = ({
	row,
	onTrigger,
	extractionStatus,
	triggerPending,
	hasExistingExtraction,
	isExtensionSupported = isExtractableExtension,
}) => {
	const tooltipId = useId();
	const extractionInProgress = extractionStatus === ExtractionStatus.Pending;
	const canAutoExtract = isExtensionSupported(row.file.extension);
	const isBlocked = extractionInProgress || triggerPending || !canAutoExtract;
	const config = hasExistingExtraction
		? {
				tooltip: "Re-extract this document",
				confirmMessage: "Are you sure you want to re-extract this document?",
				confirmButtonText: "Re-extract",
				buttonLabel: "Re-extract this document",
			}
		: {
				tooltip: "Extract this document",
				confirmMessage: "Are you sure you want to extract this document?",
				confirmButtonText: "Extract",
				buttonLabel: "Extract this document",
			};
	const tooltipContent = !canAutoExtract
		? "This file type is not supported for automatic extraction."
		: extractionInProgress || triggerPending
			? "Extraction is in progress"
			: config.tooltip;

	return (
		<>
			<span
				className="inline-flex h-6 w-6 shrink-0 items-center justify-center"
				data-tooltip-id={tooltipId}
				data-tooltip-content={tooltipContent}
			>
				<ConfirmationModalClickWrapper
					message={<div className="w-[375px] text-center">{config.confirmMessage}</div>}
					confirmButtonText={config.confirmButtonText}
					confirmationButtonColor="primary"
					onConfirm={() => onTrigger(row)}
				>
					<ClickableIcon
						className={iconButtonClass}
						aria-label={config.buttonLabel}
						disabled={isBlocked}
						type="play"
						size="small"
						color="tertiary"
					/>
				</ConfirmationModalClickWrapper>
			</span>
			<PortaledTooltip id={tooltipId} />
		</>
	);
};
